// src/components/chat/ChatInput.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, ImagePlus, X } from 'lucide-react';

const ImagePreviewModal = ({ file, onSend, onClose }) => {
  const [caption, setCaption] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleSend = async () => {
    setSending(true);
    await onSend(file, caption.trim());
    setSending(false);
    onClose();
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col bg-black/95"
      role="dialog"
      aria-modal="true"
      aria-label="Aperçu de l'image"
    >
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-4">
        <button
          onClick={onClose}
          aria-label="Annuler"
          className="p-2 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {/* Image preview */}
      <div className="flex-1 flex items-center justify-center min-h-0 px-4">
        {previewUrl && (
          <img
            src={previewUrl}
            alt="Aperçu"
            className="max-w-full max-h-full object-contain rounded-xl"
          />
        )}
      </div>

      {/* Caption + send */}
      <div className="flex-shrink-0 flex items-center gap-2 p-4">
        <input
          ref={inputRef}
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onKeyDown={handleInputKeyDown}
          disabled={sending}
          placeholder="Ajouter une légende..."
          aria-label="Légende de l'image"
          className="flex-1 px-4 py-2.5 bg-white/10 border-2 border-white/20 focus:border-rose-400 rounded-full text-white placeholder-white/40 focus:outline-none transition text-sm"
        />
        <button
          onClick={handleSend}
          disabled={sending}
          aria-label="Envoyer la photo"
          className="p-2.5 bg-gradient-to-br from-rose-500 to-fuchsia-600 text-white rounded-full hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? (
            <div className="w-[18px] h-[18px] border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Send size={18} />
          )}
        </button>
      </div>
    </div>
  );
};

const ChatInput = ({ onSend, onSendPhoto, disabled }) => {
  const [text, setText] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const fileRef = useRef(null);

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPreviewFile(file);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSendPhoto = async (file, caption) => {
    if (onSendPhoto) {
      await onSendPhoto(file, caption);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 py-2 px-3 border-t dark:border-gray-800 bg-white dark:bg-gray-950">
        <button
          data-testid="chat-btn-photo"
          onClick={() => fileRef.current?.click()}
          disabled={disabled}
          aria-label="Ajouter une photo"
          className="p-2 text-gray-400 hover:text-rose-500 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          <ImagePlus size={20} />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />

        <input
          data-testid="chat-input-text"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-label="Votre message"
          placeholder="Votre message..."
          className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-full dark:text-gray-100 focus:outline-none transition text-base sm:text-sm"
        />

        <button
          data-testid="chat-btn-send"
          onClick={handleSubmit}
          disabled={!text.trim() || disabled}
          aria-label="Envoyer le message"
          className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-rose-500 to-fuchsia-600 text-white rounded-full hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          <Send size={18} />
        </button>
      </div>

      {previewFile && (
        <ImagePreviewModal
          file={previewFile}
          onSend={handleSendPhoto}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </>
  );
};

export default ChatInput;
